'use client';

import { useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { imageSource } from '@eliadmin/core/src/utils/image-alt-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { ReviewForm } from './review-form';
import { useReviewForm } from './use-review-form';
import type { WriteReviewProduct } from './write-review';
import type { WriteReviewTexts } from './write-review-texts';

/** Figma „Overlay“ (155:4545): #0D110D at 50 %, no blur. */
const OVERLAY = cn(
  'fixed inset-0 z-50 bg-[#0D110D]/50',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none',
);

/*
 * Phone: a full-width sheet on the bottom edge. From sm: the Figma card (652 wide, radius 16, padding
 * 28 / 32, 16 between the rows, shadow 0 24 64 −16 #0D120D 25 %) centred with `inset-0 m-auto`
 * (no translate, so the zoom animation does not leave the centre). It scrolls inside when taller
 * than the viewport.
 */
const CONTENT = cn(
  'fixed z-50 flex flex-col gap-4 overflow-y-auto overscroll-contain bg-white outline-none',
  'shadow-[0_24px_64px_-16px_rgba(13,18,13,0.25)]',
  'max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[calc(100dvh-16px)] max-sm:rounded-t-2xl',
  'max-sm:px-4 max-sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))] max-sm:pt-5',
  'sm:inset-0 sm:m-auto sm:h-fit sm:max-h-[calc(100dvh-32px)] sm:w-[652px] sm:max-w-[calc(100vw-32px)] sm:rounded-2xl sm:px-8 sm:py-7',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
  'max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom',
  'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95 motion-reduce:animate-none',
);

/**
 * Figma „Modal · Napísať hodnotenie“ (154:4535) as a Radix dialog: header with the product photo
 * (56 px, border, radius 12), the title (H4), the product name and „Zavrieť“; below it `ReviewForm`.
 * Escape, the overlay and the close button dismiss it, the focus is kept inside and starts on the
 * star group (the chosen star, else the first). The form state lives here, outside the content,
 * so an unsent draft is still there when the modal is opened again.
 */
export function WriteReviewDialog({
  open,
  onOpenChange,
  product,
  texts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: WriteReviewProduct;
  texts: WriteReviewTexts;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { form, submit, resetAfterSent } = useReviewForm({
    slug: product.slug,
    texts,
    onSent: () => onOpenChange(false),
  });
  const photo = imageSource(product.image);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={OVERLAY} />
        <DialogPrimitive.Content
          ref={contentRef}
          className={CONTENT}
          onCloseAutoFocus={resetAfterSent}
          onOpenAutoFocus={event => {
            event.preventDefault();
            const stars = Array.from(
              contentRef.current?.querySelectorAll<HTMLInputElement>('[role="radiogroup"] input') ??
                [],
            );
            (stars.find(star => star.checked) ?? stars[0])?.focus();
          }}
        >
          <div className="flex items-center gap-4">
            {photo ? (
              <div className="relative size-14 shrink-0 overflow-hidden rounded-xl border border-[#E2E5DF] bg-white">
                <Image src={photo} alt="" fill sizes="56px" className="object-contain" />
              </div>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <DialogPrimitive.Title className="nt-h4 text-[#0D110D]">
                {texts.title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="nt-body-m text-[#363D36]">
                {product.name}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <IconButton icon="x" label={texts.close} variant="ghost" size="sm" />
            </DialogPrimitive.Close>
          </div>

          <ReviewForm form={form} onSubmit={submit} texts={texts} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
